import { Button, VStack } from "@chakra-ui/react"
import { useLayoutEffect, useRef } from "react"
import Pathicon from "../../pathicon/Pathicon"
import { LicensorLink, useLicensors } from "./Licensors"
import { PathCreatorRawValues } from "./state"
import { transient } from "./style"
import useLazyRandomThemeResourceImage from "./useLazyRandomThemeResourceImage"

export function useGetNewResource() {
  const [loadImage, { loading }] = useLazyRandomThemeResourceImage({
    fetchPolicy: "network-only"
  })

  // Handles choosing to create resource.
  async function getNewResource(): Promise<PathCreatorRawValues> {
    const { data } = await loadImage()
    const [defaultImage, image, burredImage] = data?.resourceImage || []

    return {
      resource: {
        id: -1,
        name: "",
        image: image,
        background_image: burredImage,
        sourceCohort: {
          id: -1,
          role: null
        }
      },
      cohort: null
    }
  }

  return { getNewResource, loading }
}

type CreateOrSelectResourceProps = {
  onSelect: (value?: PathCreatorRawValues) => void
}

function CreateOrSelectResource({ onSelect }: CreateOrSelectResourceProps) {
  const { getNewResource, loading } = useGetNewResource()
  const licensors = useLicensors()
  const createNewButtonRef = useRef<HTMLButtonElement>(null)

  useLayoutEffect(() => {
    createNewButtonRef.current?.focus({ preventScroll: true })
  }, [])

  // Handles choosing to create resource.
  async function handleCreateNewResource() {
    onSelect(await getNewResource())
  }

  // Handles choosing to select resource.
  function handleSelectExistingResource() {
    onSelect({
      resource: null,
      cohort: null
    })
  }

  return (
    <VStack w="100%" {...transient}>
      <Button
        ref={createNewButtonRef}
        colorPalette="blackAlpha"
        w="100%"
        onClick={() => handleCreateNewResource()}
        loading={loading}
      >
        <Pathicon icon="plus" />
        Create new
      </Button>
      <Button
        colorPalette="blackAlpha"
        w="100%"
        onClick={() => handleSelectExistingResource()}
        disabled={loading}
      >
        <Pathicon icon="search-list" />
        Select existing...
      </Button>
      {licensors.map((licensor) => (
        <LicensorLink
          key={licensor.id}
          licensor={licensor}
          w="100%"
          // Closing flow on licensor click.
          onClick={() => {
            onSelect()
          }}
        />
      ))}
    </VStack>
  )
}

export default CreateOrSelectResource
